import { useCurrentEditor, useEditorState } from '@tiptap/react';
import {
  Ban,
  Bold,
  CaseSensitive,
  ChevronDown,
  CodeSquare,
  CodeXml,
  Ellipsis,
  FileDown,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Italic,
  List,
  ListCollapse,
  ListOrdered,
  Maximize,
  Minimize,
  Minus,
  Quote,
  Redo,
  RemoveFormatting,
  Strikethrough,
  Subscript,
  Superscript,
  Type,
  Underline as UnderlineIcon,
  Undo,
} from 'lucide-react';
import { createContext, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDialogContainer } from '../../common/hooks/use-dialog-container';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../dropdown-menu/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../tooltip/tooltip';
import { exportRichTextToPdf } from './export/print-to-pdf';
import { ImageButton } from './image/image-button';
import { LinkButton } from './link/link-button';
import { formatShortcut } from './shortcuts';
import { TableInsertButton } from './table/table-insert-button';

// Resolved by the Toolbar root once and consumed by every Btn so each tooltip portals into
// the nearest <dialog> ancestor (when present) instead of behind it on document.body.
const ToolbarContainerContext = createContext<HTMLElement | undefined>(undefined);

interface ColorSwatch {
  value: string;
  label: string;
}

const HIGHLIGHT_SWATCHES: ColorSwatch[] = [
  { value: '#fff48a', label: 'Yellow' },
  { value: '#b8e6ff', label: 'Sky' },
  { value: '#c7f7c7', label: 'Green' },
  { value: '#ffc7e0', label: 'Pink' },
  { value: '#e0c7ff', label: 'Purple' },
  { value: '#ffd9a8', label: 'Peach' },
];

const TEXT_COLOR_SWATCHES: ColorSwatch[] = [
  { value: '#d33', label: 'Red' },
  { value: '#d97706', label: 'Orange' },
  { value: '#ca8a04', label: 'Yellow' },
  { value: '#16a34a', label: 'Green' },
  { value: '#0969da', label: 'Blue' },
  { value: '#7c3aed', label: 'Purple' },
];

interface BtnProps {
  active?: boolean;
  onClick: () => void;
  title: string;
  shortcut?: string;
  disabled?: boolean;
  children: React.ReactNode;
}

const Shortcut = ({ keys }: { keys: string }) => (
  <span className="ml-auto pl-4 text-gray-3 text-xs">{formatShortcut(keys)}</span>
);

const Btn = ({ active, onClick, title, shortcut, disabled, children }: BtnProps) => {
  const container = useContext(ToolbarContainerContext);
  return (
    <Tooltip>
      <TooltipTrigger
        type="button"
        aria-label={title}
        aria-pressed={active}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : undefined}
        onMouseDown={(event) => event.preventDefault()}
        onClick={disabled ? undefined : onClick}
        className="tiptap-toolbar-button"
      >
        {children}
      </TooltipTrigger>
      <TooltipContent container={container}>
        {title}
        {shortcut && <span className="ml-1.5 text-gray-3">{formatShortcut(shortcut)}</span>}
      </TooltipContent>
    </Tooltip>
  );
};

interface ToolbarProps {
  /** True while the editor is maximized. Drives the toggle icon/label. */
  maximized?: boolean;
  /** When provided, renders the maximize toggle at the far right of the toolbar. */
  onToggleMaximize?: () => void;
}

export const Toolbar = ({ maximized, onToggleMaximize }: ToolbarProps) => {
  const { t } = useTranslation();
  const { editor } = useCurrentEditor();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const tooltipContainer = useDialogContainer(toolbarRef);

  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      isBold: e?.isActive('bold') ?? false,
      isItalic: e?.isActive('italic') ?? false,
      isUnderline: e?.isActive('underline') ?? false,
      isStrike: e?.isActive('strike') ?? false,
      isCode: e?.isActive('code') ?? false,
      isSubscript: e?.isActive('subscript') ?? false,
      isSuperscript: e?.isActive('superscript') ?? false,
      isH1: e?.isActive('heading', { level: 1 }) ?? false,
      isH2: e?.isActive('heading', { level: 2 }) ?? false,
      isH3: e?.isActive('heading', { level: 3 }) ?? false,
      isBulletList: e?.isActive('bulletList') ?? false,
      isOrderedList: e?.isActive('orderedList') ?? false,
      isBlockquote: e?.isActive('blockquote') ?? false,
      isCodeBlock: e?.isActive('codeBlock') ?? false,
      isDetails: e?.isActive('details') ?? false,
      isLink: e?.isActive('link') ?? false,
      isEmpty: e?.isEmpty ?? true,
      canUndo: e?.can().undo() ?? false,
      canRedo: e?.can().redo() ?? false,
      textColor: (e?.getAttributes('textStyle').color as string | undefined) ?? null,
      highlightColor: (e?.getAttributes('highlight').color as string | undefined) ?? null,
    }),
  });

  if (!editor || !state) {
    return null;
  }

  // Text styles trigger icon mirrors the cursor's current block style so users can see what's applied without opening the menu.
  const TextStyleIcon = state.isH1 ? Heading1 : state.isH2 ? Heading2 : state.isH3 ? Heading3 : CaseSensitive;

  return (
    <TooltipProvider>
      <ToolbarContainerContext.Provider value={tooltipContainer}>
        <div ref={toolbarRef} className="tiptap-toolbar" role="toolbar">
          <div className="tiptap-toolbar-group">
            <Btn
              title={t('bold')}
              shortcut="Mod-B"
              active={state.isBold}
              onClick={() => editor.chain().focus().toggleBold().run()}
            >
              <Bold />
            </Btn>
            <Btn
              title={t('italic')}
              shortcut="Mod-I"
              active={state.isItalic}
              onClick={() => editor.chain().focus().toggleItalic().run()}
            >
              <Italic />
            </Btn>
            <Btn
              title={t('underline')}
              shortcut="Mod-U"
              active={state.isUnderline}
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            >
              <UnderlineIcon />
            </Btn>

            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger
                  render={<DropdownMenuTrigger />}
                  className="tiptap-toolbar-button"
                  aria-label={t('text-styles')}
                  onMouseDown={(event) => event.preventDefault()}
                >
                  <TextStyleIcon />
                </TooltipTrigger>
                <TooltipContent container={tooltipContainer}>{t('text-styles')}</TooltipContent>
              </Tooltip>
              <DropdownMenuContent container={tooltipContainer} className="min-w-[200px]">
                <DropdownMenuItem onClick={() => editor.chain().focus().setParagraph().run()}>
                  <CaseSensitive />
                  {t('normal-text')}
                  <Shortcut keys="Mod-Alt-0" />
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}>
                  <Heading1 />
                  {t('heading-1')}
                  <Shortcut keys="Mod-Alt-1" />
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
                  <Heading2 />
                  {t('heading-2')}
                  <Shortcut keys="Mod-Alt-2" />
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
                  <Heading3 />
                  {t('heading-3')}
                  <Shortcut keys="Mod-Alt-3" />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger
                  render={<DropdownMenuTrigger />}
                  className="tiptap-toolbar-button"
                  aria-label={t('format-text')}
                  onMouseDown={(event) => event.preventDefault()}
                >
                  <ChevronDown />
                </TooltipTrigger>
                <TooltipContent container={tooltipContainer}>{t('format-text')}</TooltipContent>
              </Tooltip>
              <DropdownMenuContent container={tooltipContainer} className="min-w-[200px]">
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleStrike().run()}>
                  <Strikethrough />
                  {t('strikethrough')}
                  <Shortcut keys="Mod-Shift-S" />
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleCode().run()}>
                  <CodeXml />
                  {t('code')}
                  <Shortcut keys="Mod-E" />
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleSubscript().run()}>
                  <Subscript />
                  {t('subscript')}
                  <Shortcut keys="Mod-," />
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleSuperscript().run()}>
                  <Superscript />
                  {t('superscript')}
                  <Shortcut keys="Mod-." />
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}>
                  <RemoveFormatting />
                  {t('clear-formatting')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="tiptap-toolbar-group">
            <Btn
              title={t('bullet-list')}
              shortcut="Mod-Shift-8"
              active={state.isBulletList}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <List />
            </Btn>
            <Btn
              title={t('numbered-list')}
              shortcut="Mod-Shift-7"
              active={state.isOrderedList}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              <ListOrdered />
            </Btn>
          </div>

          <div className="tiptap-toolbar-group">
            <LinkButton active={state.isLink} />
            <ImageButton />
            <div className="tiptap-toolbar-hide-touch">
              <TableInsertButton />
            </div>
            <Btn
              title={t('export-as-pdf')}
              disabled={state.isEmpty}
              // Swallow: print() can reject (pop-up/permission blocks) — there's no actionable
              // follow-up and odin-components has no logger to report to.
              onClick={() => exportRichTextToPdf(editor.getHTML()).catch(() => undefined)}
            >
              <FileDown />
            </Btn>
          </div>

          <div className="tiptap-toolbar-group">
            <DropdownMenu>
              <DropdownMenuTrigger
                className="tiptap-toolbar-button"
                aria-label={t('more-options')}
                onMouseDown={(event) => event.preventDefault()}
              >
                <Ellipsis />
              </DropdownMenuTrigger>
              <DropdownMenuContent container={tooltipContainer} className="min-w-[240px]">
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleBlockquote().run()}>
                  <Quote />
                  {t('blockquote')}
                  <Shortcut keys="Mod-Shift-B" />
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => editor.chain().focus().toggleCodeBlock().run()}>
                  <CodeSquare />
                  {t('code-block')}
                  <Shortcut keys="Mod-Alt-C" />
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    state.isDetails
                      ? editor.chain().focus().unsetDetails().run()
                      : editor.chain().focus().setDetails().run()
                  }
                >
                  <ListCollapse />
                  {t('collapsible-section')}
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem onClick={() => editor.chain().focus().setHorizontalRule().run()}>
                  <Minus />
                  {t('insert-horizontal-rule')}
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Highlighter />
                    {t('highlight-colour')}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent container={tooltipContainer}>
                    <DropdownMenuItem onClick={() => editor.chain().focus().unsetHighlight().run()}>
                      <Ban />
                      {t('remove-highlight')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <div className="tiptap-color-grid">
                      {HIGHLIGHT_SWATCHES.map((s) => (
                        <DropdownMenuItem
                          key={s.value}
                          aria-label={s.label}
                          aria-pressed={state.highlightColor?.toLowerCase() === s.value.toLowerCase()}
                          className="tiptap-color-swatch p-0"
                          style={{ backgroundColor: s.value }}
                          onClick={() => editor.chain().focus().setHighlight({ color: s.value }).run()}
                        />
                      ))}
                    </div>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Type />
                    {t('text-colour')}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent container={tooltipContainer}>
                    <DropdownMenuItem onClick={() => editor.chain().focus().unsetColor().run()}>
                      <Ban />
                      {t('remove-colour')}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <div className="tiptap-color-grid">
                      {TEXT_COLOR_SWATCHES.map((s) => (
                        <DropdownMenuItem
                          key={s.value}
                          aria-label={s.label}
                          aria-pressed={state.textColor?.toLowerCase() === s.value.toLowerCase()}
                          className="tiptap-color-swatch p-0"
                          style={{ backgroundColor: s.value }}
                          onClick={() => editor.chain().focus().setColor(s.value).run()}
                        />
                      ))}
                    </div>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                <DropdownMenuSeparator />

                <DropdownMenuItem disabled={!state.canUndo} onClick={() => editor.chain().focus().undo().run()}>
                  <Undo />
                  {t('undo')}
                  <Shortcut keys="Mod-Z" />
                </DropdownMenuItem>
                <DropdownMenuItem disabled={!state.canRedo} onClick={() => editor.chain().focus().redo().run()}>
                  <Redo />
                  {t('redo')}
                  <Shortcut keys="Mod-Shift-Z" />
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {onToggleMaximize && (
            <div className="tiptap-toolbar-group tiptap-toolbar-group-end">
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={onToggleMaximize}
                className="tiptap-toolbar-button"
                aria-label={maximized ? t('editor-restore') : t('editor-maximise')}
              >
                {maximized ? <Minimize /> : <Maximize />}
              </button>
            </div>
          )}
        </div>
      </ToolbarContainerContext.Provider>
    </TooltipProvider>
  );
};
